import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  AlertTriangle, CheckCircle2, Loader2, Users, Briefcase, MapPin, Sparkles, Globe, Database,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  mergePreviewEntity, mergeApplyEntity, mergeSuggestEntity,
  type EntityConflictField, type FieldResolution, type MergeFieldSuggestion, type MergeSummary,
} from "@/services/api/entityApi";

export interface ExistingEntityMatch {
  entity_id: string;
  entity_name: string;
  entity_type: string;
  /** Comparison context — without these, two same-named entities (e.g. two "Chaithanya"s
   * in different roles) are indistinguishable in the picker beyond a bare name + score. */
  entity_description: string | null;
  person_role: string | null;
  entity_location: string | null;
  score: number;
}

function asText(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

interface ConflictReviewModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful merge-apply so the caller can re-fetch the entity and
   * clear its "N fields need review" banner. finalEntityId is the entity the merge
   * was actually applied to. */
  onResolved: (finalEntityId: string) => void;
  /** Multi-match mode only — called when the user picks "None of these" instead of any
   * candidate match, so the caller can fall through to creating a new entity. */
  onNoMatch?: () => void;

  // ── Mode A: re-scrape a single known entity's own extraction_url (unchanged, backlog #18) ──
  entityId?: string;

  // ── Mode B: diff fresh candidate data against one of several possible existing matches
  // (PROJECT-077 T0112/T0117) — used when a found-a-possible-duplicate surface (search-
  // resolver, self-match) has candidate data in hand but doesn't yet know which, if any,
  // existing entity it corresponds to. ──
  matches?: ExistingEntityMatch[];
  candidateData?: Record<string, unknown>;
  candidateSource?: string;
  /** PROJECT-077b T0104/T0109 — set when candidateData belongs to a second, already-
   * existing entity (not just fresh scrape/search text) that should be retired and
   * folded into whichever match the user picks — e.g. the dashboard's "possible
   * duplicate" banner (T0110), where both sides are real entity rows. Triggers the
   * full relationship/permission/integration re-point sweep on Apply, and the "what's
   * about to move" / "what just moved" copy below. */
  mergedEntityId?: string;
}

const FIELD_LABELS: Record<string, string> = {
  entity_name: "Name",
  entity_description: "Description",
  entity_type_sub: "Sub-type",
  entity_offering_type: "Offering type",
  entity_offering_delivery: "Offering delivery",
  industry: "Industry",
  target_market: "Target market",
  brand_tone: "Brand tone",
  detected_language: "Language",
  target_customer: "Target customer",
  problem_solved: "Problem solved",
  pricing_info: "Pricing",
  person_role: "Role",
  entity_location: "Location",
};

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

function fieldValueText(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Same as fieldValueText but for pre-filling the editable box — an empty string rather
 * than the "—" placeholder, which would otherwise be saved as the literal field value. */
function asEditableText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** The A/B/Merge screen resolves to exactly one of three outcomes. Only "merge" has a
 * second screen behind it (the per-field diff); "A" hands straight back to the caller via
 * onNoMatch, and "B" applies immediately. */
type Decision = "merge";

/**
 * Shared diff/merge review popup (PROJECT-077 T0112/T0115 — one popup, reused by every
 * "found a possible duplicate" surface instead of each building its own):
 *   - Re-scrape conflict review: "Refresh from source" on EntityDetailPage, or an auto-open
 *     when the extraction websocket emits node_conflicts_detected (backlog #18). Uses
 *     `entityId` — fetches conflicts by re-scraping that entity's own extraction_url.
 *   - Found-a-duplicate review: manual-search-resolver / self-match / duplicate banner hand
 *     this fresh `candidateData` plus a list of `matches` it might correspond to.
 *
 * Duplicate-review flow (PROJECT-077b): pick which existing entity this is (skipped when
 * there's only one), then make ONE explicit call on the A-vs-B screen — keep the incoming
 * version, keep the saved one, or hand it to the AI merge — rather than being dropped
 * straight into a per-field checklist with no stated choice. The per-field diff is now the
 * inside of the "Merge with AI" option, not the entry point.
 */
export function ConflictReviewModal({
  open, onClose, onResolved, onNoMatch,
  entityId, matches, candidateData, candidateSource, mergedEntityId,
}: ConflictReviewModalProps) {
  const isMultiMatch = matches !== undefined;
  const isEntityMerge = Boolean(mergedEntityId);

  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(!isMultiMatch);
  const [error, setError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<Record<string, EntityConflictField>>({});
  const [autoFilledFields, setAutoFilledFields] = useState<string[]>([]);
  const [resolutions, setResolutions] = useState<Record<string, FieldResolution>>({});
  // Free-text values for fields resolved as "custom" — keyed by field, seeded from the
  // advisor's merged_value draft (or the current value when it returns none) the first
  // time the user picks that option.
  const [customValues, setCustomValues] = useState<Record<string, string>>({});
  // Which field's "Generate with AI" is currently in flight, and any per-field note from
  // the last attempt (e.g. the advisor declining to combine a location).
  const [draftingField, setDraftingField] = useState<string | null>(null);
  const [draftNotes, setDraftNotes] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  // PROJECT-077b — the A/B/Merge call. null = the choice hasn't been made yet (show the
  // decision screen); "merge" = show the per-field diff. The two whole-version options
  // never render the diff at all — they apply straight away.
  const [decision, setDecision] = useState<Decision | null>(null);
  const [keepingExisting, setKeepingExisting] = useState(false);
  // PROJECT-077b T0109 — set only after a successful merge that retired a second entity;
  // shows a brief "here's what moved" confirmation before the modal closes, so the user
  // isn't left wondering whether their data survived the merge.
  const [mergeSummary, setMergeSummary] = useState<MergeSummary | null>(null);
  // AI "which value is better" recommendation per field (PROJECT-077) — fetched separately
  // from the diff itself so a slow/failed suggestion call never delays the popup opening.
  const [suggestions, setSuggestions] = useState<Record<string, MergeFieldSuggestion>>({});
  // Ref, not state: only read at the moment a suggestion arrives, to decide whether it's
  // still safe to auto-bias that field's default pick. A ref always reads the latest click,
  // even one that lands mid-fetch — state here would read a stale closure instead.
  const touchedFieldsRef = useRef<Set<string>>(new Set());
  // What the reset effect below has already initialised for. Null while closed, so the
  // next open always resets; otherwise the subject's id, so a genuinely different subject
  // resets but a re-render does not.
  const initialisedForRef = useRef<string | null>(null);

  // Reset per-open state — a fresh open (new candidate/entity) shouldn't carry over a
  // prior run's picked target or resolutions.
  //
  // Fires ONLY on a closed→open transition, or when `entityId` changes to a different
  // subject while open. It deliberately does NOT depend on `candidateData`: every caller
  // builds that inline as an object literal, so it's a new reference on each parent
  // render. Depending on it meant any unrelated re-render of the parent wiped this state —
  // most visibly when the browser tab regained focus and a refetch re-rendered the wizard,
  // throwing the user from mid-review back to the "This might already exist" picker and
  // losing their picked match, field resolutions and custom text.
  useEffect(() => {
    if (!open) {
      initialisedForRef.current = null;
      return;
    }
    const subject = entityId ?? "candidate-review";
    if (initialisedForRef.current === subject) return;
    initialisedForRef.current = subject;

    setSelectedTargetId(null);
    setError(null);
    setConflicts({});
    setAutoFilledFields([]);
    setResolutions({});
    setCustomValues({});
    setDraftingField(null);
    setDraftNotes({});
    setSuggestions({});
    setDecision(null);
    setKeepingExisting(false);
    setMergeSummary(null);
    touchedFieldsRef.current = new Set();
  }, [open, entityId]);

  // A one-item picker is a click that asks nothing — the single candidate IS the
  // comparison. Auto-select it so the popup opens directly on the A/B/Merge choice.
  // ("this is actually someone else" stays reachable there via onNoMatch.)
  const singleMatchId = isMultiMatch && matches!.length === 1 ? matches![0].entity_id : null;
  useEffect(() => {
    if (open && singleMatchId) setSelectedTargetId(singleMatchId);
  }, [open, singleMatchId]);

  const targetEntityId = isMultiMatch ? selectedTargetId : entityId ?? null;

  useEffect(() => {
    if (!open || !targetEntityId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDecision(null);
    mergePreviewEntity(targetEntityId, candidateData, candidateSource)
      .then((res) => {
        if (cancelled) return;
        if (!res.status) {
          setError(res.message || "Failed to preview merge");
          return;
        }
        const fetchedConflicts = res.data?.conflicts ?? {};
        setConflicts(fetchedConflicts);
        setAutoFilledFields(res.data?.auto_filled_fields ?? []);
        // Default every field to "keep_old" — don't silently prefer new data over
        // what may have been a deliberate human edit.
        setResolutions(
          Object.fromEntries(Object.keys(fetchedConflicts).map((f) => [f, "keep_old" as const])),
        );
      })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "Failed to preview merge"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetEntityId]);

  const conflictFields = Object.keys(conflicts);
  const hasConflicts = conflictFields.length > 0;

  // AI suggestion fetch — deliberately separate from the effect above so a slow/failed
  // suggestion call can never delay the diff itself from rendering. Fires once conflicts
  // are known; best-effort throughout, never surfaced as a modal-level error.
  useEffect(() => {
    if (!open || !targetEntityId || !hasConflicts) return;
    let cancelled = false;
    mergeSuggestEntity(targetEntityId, conflicts)
      .then((res) => {
        if (cancelled || !res.status) return;
        const fetched = res.data?.suggestions ?? {};
        setSuggestions(fetched);
        // Bias the default pick toward the AI's recommendation — but only for fields the
        // user hasn't already clicked themselves; never override a deliberate choice.
        setResolutions((prev) => {
          const next = { ...prev };
          for (const [field, s] of Object.entries(fetched)) {
            if (field in next && !touchedFieldsRef.current.has(field)) {
              next[field] = s.recommendation;
            }
          }
          return next;
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetEntityId, hasConflicts]);

  // `override` lets the whole-version options (A / B) apply without first pushing their
  // resolutions through setState — React wouldn't have committed them by the time this
  // reads `resolutions`, so it would silently submit the previous picks instead.
  const handleApply = async (override?: Record<string, FieldResolution>) => {
    if (!targetEntityId) return;
    setSubmitting(true);
    setError(null);
    try {
      // Send only non-blank custom values. A field marked "custom" with an empty box is
      // almost always an unfinished edit rather than an intent to erase the stored value,
      // and the backend falls back to keep_old for any "custom" it has no value for.
      const trimmedCustom = Object.fromEntries(
        Object.entries(customValues).filter(([, v]) => v.trim().length > 0),
      );
      const res = await mergeApplyEntity(
        targetEntityId, override ?? resolutions, candidateSource, mergedEntityId, trimmedCustom,
      );
      if (!res.status) {
        setError(res.message || "Failed to apply merge");
        return;
      }
      // For a real two-entity merge, show what moved before closing rather than
      // vanishing the popup the instant Apply succeeds — the user just asked to fold
      // one profile into another and should see it actually happened, safely.
      if (isEntityMerge && res.data?.merge_summary) {
        setMergeSummary(res.data.merge_summary);
        return;
      }
      onResolved(targetEntityId);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to apply merge");
    } finally {
      setSubmitting(false);
      setKeepingExisting(false);
    }
  };

  /** Option B — "this is the one I was looking for". Resolves every conflicting field to
   * keep_old, which changes no stored value but does clear pending_conflicts so B isn't
   * left wearing an unresolved "N fields need review" banner. Still goes through
   * merge-apply rather than straight to onResolved: on a real two-entity merge that call
   * is what runs merge_entity_into's relationship/permission/integration re-point sweep. */
  const applyWholeVersion = (_choice: "use_existing") => {
    setKeepingExisting(true);
    handleApply(Object.fromEntries(conflictFields.map((f) => [f, "keep_old" as const])));
  };

  /**
   * On-demand "combine A and B into one value" for a single field. Reuses
   * /entity/merge-suggest with a one-field conflicts map rather than adding a second
   * endpoint — that call already drafts merged_value and is already permission-gated, so
   * the only thing missing was a way to ask for it again (MR0q).
   *
   * The batch call on open pre-fills this box when it returns a draft; this button covers
   * the cases that can't: the batch call failed or was still in flight, or the user has
   * since edited the text and wants a fresh combination.
   */
  const handleGenerateDraft = async (field: string) => {
    if (!targetEntityId || !conflicts[field]) return;
    setDraftingField(field);
    setDraftNotes((n) => { const { [field]: _drop, ...rest } = n; return rest; });
    try {
      const res = await mergeSuggestEntity(targetEntityId, { [field]: conflicts[field] });
      const suggestion = res.data?.suggestions?.[field];
      if (suggestion?.merged_value) {
        setCustomValues((c) => ({ ...c, [field]: suggestion.merged_value as string }));
        setSuggestions((prev) => ({ ...prev, [field]: suggestion }));
      } else {
        // Deliberately does NOT reuse suggestion.reason here: that sentence explains which
        // SIDE the advisor recommends, not why it declined to combine, so surfacing it
        // under a "couldn't combine" heading produced a plainly contradictory note (e.g.
        // "Couldn't combine these — the current value is more direct...", which is an
        // argument for a pick, not a refusal).
        setDraftNotes((n) => ({
          ...n,
          [field]: "No combined draft came back for this field — short values like names, locations and dates can't be merged. Type your own above, or pick a side.",
        }));
      }
    } catch {
      setDraftNotes((n) => ({ ...n, [field]: "Couldn't reach the AI just now — try again." }));
    } finally {
      setDraftingField(null);
    }
  };

  const handleDoneAfterMerge = () => {
    if (targetEntityId) onResolved(targetEntityId);
    onClose();
  };

  const handleNoMatch = () => {
    onNoMatch?.();
    onClose();
  };

  // ── Which screen is showing ──────────────────────────────────────────────────
  const showPicker = isMultiMatch && !targetEntityId;
  const ready = Boolean(targetEntityId) && !loading && !error && !mergeSummary;
  // A/B/Merge only applies to the duplicate-review path — Mode A (re-scrape of one known
  // entity) has no "incoming entity" to sit opposite it, so it keeps going straight to
  // the field diff exactly as before.
  const showDecision = ready && isMultiMatch && !decision;
  const showDiff = ready && !showDecision;
  // Only meaningful with >1 candidate; with a single auto-selected match there's no
  // picker behind us to return to.
  const canGoBackToPicker = isMultiMatch && matches!.length > 1;

  const handleBack = () => {
    if (decision) { setDecision(null); return; }
    if (canGoBackToPicker) { setSelectedTargetId(null); return; }
    onClose();
  };

  const backLabel = decision || canGoBackToPicker ? "Back" : hasConflicts ? "Cancel" : "Close";

  const newName = asText(candidateData?.entity_name);
  const newDescription = asText(candidateData?.entity_description);
  const newRole = asText(candidateData?.person_role);
  const newLocation = asText(candidateData?.entity_location);
  const selectedMatch = matches?.find((m) => m.entity_id === targetEntityId) ?? null;

  const title = mergeSummary
    ? "Merged"
    : showPicker
      ? "This might already exist"
      : showDecision
        ? "Which version should we keep?"
        : "Review updated fields";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="p-0 flex flex-col max-h-[88vh] gap-0 max-w-2xl">
        <DialogHeader className="flex-shrink-0 px-5 pt-5 pb-4 border-b border-border/50">
          <DialogTitle className="text-base font-semibold leading-tight">{title}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {mergeSummary && (
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-2 rounded-lg border border-green-500/25 bg-green-500/[0.04] px-3.5 py-3">
                <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">
                  The duplicate profile was archived, not deleted — its data moved to the one you kept.
                </p>
              </div>
              <ul className="flex flex-col gap-1.5 text-xs text-muted-foreground/80 pl-1">
                {mergeSummary.relationships_moved > 0 && (
                  <li>{mergeSummary.relationships_moved} relationship{mergeSummary.relationships_moved === 1 ? "" : "s"} moved over</li>
                )}
                {mergeSummary.permissions_moved > 0 && (
                  <li>{mergeSummary.permissions_moved} permission{mergeSummary.permissions_moved === 1 ? "" : "s"} moved over</li>
                )}
                {mergeSummary.integrations_moved > 0 && (
                  <li>{mergeSummary.integrations_moved} connected integration{mergeSummary.integrations_moved === 1 ? "" : "s"} moved over</li>
                )}
                {mergeSummary.integrations_flagged_for_review.length > 0 && (
                  <li className="text-amber-500/90 flex items-start gap-1">
                    <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                    {mergeSummary.integrations_flagged_for_review.length} integration{mergeSummary.integrations_flagged_for_review.length === 1 ? "" : "s"} need manual review — both profiles had an active connection of the same kind
                  </li>
                )}
              </ul>
            </div>
          )}

          {!mergeSummary && showPicker && (
            <div className="flex flex-col gap-4">
              <p className="text-xs text-muted-foreground/70 leading-relaxed">
                We found {matches!.length} existing {matches!.length === 1 ? "entity that looks" : "entities that look"} similar. Compare the details below and pick the one that's really the same — or none, if this is genuinely new.
              </p>

              {(newName || newDescription || newRole || newLocation) && (
                <div className="rounded-lg border border-blue-500/25 bg-blue-500/[0.04] p-3.5 flex flex-col gap-1.5">
                  <p className="text-label-2xs font-semibold uppercase tracking-wide text-blue-400/80">What we found</p>
                  <p className="text-sm font-medium text-foreground">{newName ?? "—"}</p>
                  {(newRole || newLocation) && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-label-xs text-muted-foreground/80">
                      {newRole && <span className="flex items-center gap-1"><Briefcase className="h-3 w-3 shrink-0" />{newRole}</span>}
                      {newLocation && <span className="flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" />{newLocation}</span>}
                    </div>
                  )}
                  {newDescription && (
                    <p className="text-xs text-muted-foreground/70 leading-relaxed">{newDescription}</p>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-2">
                <p className="text-label-2xs font-semibold uppercase tracking-wide text-subtle-foreground">
                  Compared against {matches!.length === 1 ? "this existing entity" : `${matches!.length} existing entities`}
                </p>
                {matches!.map((m) => (
                  <button
                    key={m.entity_id}
                    type="button"
                    onClick={() => setSelectedTargetId(m.entity_id)}
                    className="flex flex-col gap-1.5 text-left rounded-lg border border-border/50 hover:border-blue-500/40 hover:bg-blue-500/[0.03] px-3.5 py-3 transition-all"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Users className="h-3.5 w-3.5 text-subtle-foreground shrink-0" />
                        <span className="text-sm font-medium text-foreground truncate">{m.entity_name}</span>
                      </div>
                      <Badge variant="outline" className="text-label-2xs shrink-0">
                        {Math.round(m.score * 100)}% match
                      </Badge>
                    </div>
                    {(m.person_role || m.entity_location) && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-6 text-label-xs text-muted-foreground/70">
                        {m.person_role && <span className="flex items-center gap-1"><Briefcase className="h-3 w-3 shrink-0" />{m.person_role}</span>}
                        {m.entity_location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" />{m.entity_location}</span>}
                      </div>
                    )}
                    {m.entity_description && (
                      <p className="pl-6 text-label-xs text-muted-foreground/70 leading-relaxed line-clamp-2">{m.entity_description}</p>
                    )}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={handleNoMatch}
                className="text-xs text-muted-foreground/70 hover:text-muted-foreground self-start mt-1"
              >
                None of these — this is a new entity
              </button>
            </div>
          )}

          {!mergeSummary && !showPicker && loading && (
            <div className="flex flex-col items-center justify-center gap-2 py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/70" />
              <p className="text-sm text-muted-foreground/70">
                {isMultiMatch ? "Comparing…" : "Re-scanning the source…"}
              </p>
            </div>
          )}

          {!mergeSummary && !showPicker && !loading && error && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5">
              <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* ── A vs B vs Merge — the actual decision (PROJECT-077b) ────────────── */}
          {showDecision && (
            <div className="flex flex-col gap-4">
              <p className="text-xs text-muted-foreground/70 leading-relaxed">
                {hasConflicts
                  ? `A is what we just found, B is what's already in Raisy — ${conflictFields.length} field${conflictFields.length === 1 ? " differs" : "s differ"}. Keep them as two records, use B, or combine them into one.`
                  : "A is what we just found, B is what's already in Raisy. Nothing conflicts between them — keep them as two records, or use B as the single record."}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <VersionCard
                  tone="incoming"
                  letter="A"
                  heading="Incoming"
                  subheading="Just found"
                  icon={<Globe className="h-3 w-3 shrink-0" />}
                  name={newName}
                  role={newRole}
                  location={newLocation}
                  description={newDescription}
                />
                <VersionCard
                  tone="existing"
                  letter="B"
                  heading="Already in Raisy"
                  subheading={selectedMatch ? `${Math.round(selectedMatch.score * 100)}% match` : "Saved profile"}
                  icon={<Database className="h-3 w-3 shrink-0" />}
                  name={selectedMatch?.entity_name ?? null}
                  role={selectedMatch?.person_role ?? null}
                  location={selectedMatch?.entity_location ?? null}
                  description={selectedMatch?.entity_description ?? null}
                />
              </div>

              {hasConflicts && (
                <div className="flex flex-col gap-1.5">
                  <p className="text-label-2xs font-semibold uppercase tracking-wide text-subtle-foreground">
                    Fields that differ
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {conflictFields.map((f) => (
                      <Badge key={f} variant="outline" className="text-label-2xs border-border/50 text-muted-foreground/80">
                        {fieldLabel(f)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {isEntityMerge && (
                <p className="text-xs text-muted-foreground/70 leading-relaxed">
                  If you combine them, relationships, permissions and connected integrations move to the profile you keep and the duplicate is archived — never deleted. Keeping them separate changes nothing on either one.
                </p>
              )}

              {/* A / B / Merge. The axis is how many records survive, not which text wins,
                  so every option leads with that. Picking A does NOT overwrite B — A becomes
                  its own new record and B is left untouched; only B and Merge end with a
                  single record. Getting that backwards is the whole reason this screen
                  exists rather than dropping straight into a field checklist. */}
              <div className="flex flex-col gap-2">
                {onNoMatch && (
                  <ChoiceRow
                    icon={<Globe className="h-4 w-4 text-blue-400" />}
                    title={isEntityMerge ? "A — keep both as separate records" : "A — add as a new record"}
                    subtitle={
                      isEntityMerge
                        ? "Two records. Nothing is merged or overwritten — they stay separate entities that happen to look alike."
                        : "Two records. A is saved as its own new entity and B is left exactly as it is."
                    }
                    onClick={handleNoMatch}
                    disabled={submitting}
                  />
                )}
                <ChoiceRow
                  icon={<Database className="h-4 w-4 text-muted-foreground" />}
                  title="B — this is the one I was looking for"
                  subtitle="One record. B is used as-is; nothing about it is overwritten and no second record is created."
                  onClick={() => applyWholeVersion("use_existing")}
                  disabled={submitting}
                  busy={keepingExisting}
                />
                {hasConflicts && (
                  <ChoiceRow
                    icon={<Sparkles className="h-4 w-4 text-blue-400" />}
                    title="Merge — combine them into one"
                    subtitle="One record. Fold A's details into B field by field, with an AI recommendation for each."
                    badge="Recommended"
                    onClick={() => setDecision("merge")}
                    disabled={submitting}
                  />
                )}
              </div>
            </div>
          )}

          {showDiff && !hasConflicts && (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <p className="text-sm text-foreground">
                No conflicts
                {autoFilledFields.length > 0 && ` — ${autoFilledFields.length} field${autoFilledFields.length === 1 ? "" : "s"} updated automatically`}
              </p>
            </div>
          )}

          {showDiff && hasConflicts && (
            <div className="flex flex-col gap-3">
              <p className="text-xs text-muted-foreground/70 leading-relaxed">
                {isMultiMatch
                  ? "Choose which value to keep for each field."
                  : "The source has changed since this was last saved. Choose which value to keep for each field."}
              </p>
              {conflictFields.map((field) => {
                const { old: oldValue, new: newValue } = conflicts[field];
                const choice = resolutions[field] ?? "keep_old";
                const suggestion = suggestions[field];
                const markTouched = (f: string) => { touchedFieldsRef.current.add(f); };
                return (
                  <div key={field} className="rounded-lg border border-border/50 p-3 flex flex-col gap-2">
                    <p className="text-xs font-semibold text-foreground">{fieldLabel(field)}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => { markTouched(field); setResolutions((r) => ({ ...r, [field]: "keep_old" })); }}
                        className={cn(
                          "text-left rounded-md border px-2.5 py-2 transition-all",
                          choice === "keep_old"
                            ? "border-blue-500/60 bg-blue-500/10"
                            : "border-border/40 hover:border-border/70",
                        )}
                      >
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <p className="text-label-2xs font-semibold uppercase tracking-wide text-subtle-foreground">
                            {isMultiMatch ? "B · Keep current" : "Keep current"}
                          </p>
                          {suggestion?.recommendation === "keep_old" && (
                            <Badge variant="outline" className="text-label-2xs gap-0.5 px-1.5 py-0 border-blue-500/40 text-blue-400 shrink-0">
                              <Sparkles className="h-2.5 w-2.5" />
                              AI pick
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-foreground/90 break-words">{fieldValueText(oldValue)}</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => { markTouched(field); setResolutions((r) => ({ ...r, [field]: "take_new" })); }}
                        className={cn(
                          "text-left rounded-md border px-2.5 py-2 transition-all",
                          choice === "take_new"
                            ? "border-blue-500/60 bg-blue-500/10"
                            : "border-border/40 hover:border-border/70",
                        )}
                      >
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <p className="text-label-2xs font-semibold uppercase tracking-wide text-subtle-foreground">
                            {isMultiMatch ? "A · Use new" : "Use new"}
                          </p>
                          {suggestion?.recommendation === "take_new" && (
                            <Badge variant="outline" className="text-label-2xs gap-0.5 px-1.5 py-0 border-blue-500/40 text-blue-400 shrink-0">
                              <Sparkles className="h-2.5 w-2.5" />
                              AI pick
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-foreground/90 break-words">{fieldValueText(newValue)}</p>
                      </button>
                    </div>
                    {/* Third option: write your own. Picking a side always discards
                        whatever the other side knew, which for a description is usually
                        the wrong trade — merged_value gives the user a combined draft to
                        start from instead of retyping it. Offered on every field, since
                        "neither of these is right" is always a legitimate answer. */}
                    <button
                      type="button"
                      onClick={() => {
                        markTouched(field);
                        setCustomValues((c) => (
                          field in c
                            ? c
                            : { ...c, [field]: suggestion?.merged_value ?? asEditableText(oldValue) }
                        ));
                        setResolutions((r) => ({ ...r, [field]: "custom" }));
                      }}
                      className={cn(
                        "text-left rounded-md border px-2.5 py-2 transition-all",
                        choice === "custom"
                          ? "border-blue-500/60 bg-blue-500/10"
                          : "border-border/40 hover:border-border/70",
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-label-2xs font-semibold uppercase tracking-wide text-subtle-foreground">
                          Write my own
                        </p>
                        {suggestion?.merged_value && (
                          <Badge variant="outline" className="text-label-2xs gap-0.5 px-1.5 py-0 border-blue-500/40 text-blue-400 shrink-0">
                            <Sparkles className="h-2.5 w-2.5" />
                            AI merged draft
                          </Badge>
                        )}
                      </div>
                      {choice !== "custom" && (
                        <p className="text-xs text-muted-foreground/70 mt-0.5">
                          {suggestion?.merged_value
                            ? "Start from a draft combining both, then edit it."
                            : "Enter a different value instead of picking a side."}
                        </p>
                      )}
                    </button>

                    {choice === "custom" && (
                      <div className="flex flex-col gap-1.5">
                        <Textarea
                          autoFocus
                          value={customValues[field] ?? ""}
                          onChange={(e) => setCustomValues((c) => ({ ...c, [field]: e.target.value }))}
                          placeholder="Type the value you want to save, or combine both with AI below…"
                          className="text-xs min-h-[80px]"
                        />
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 text-label-xs gap-1.5"
                            onClick={() => handleGenerateDraft(field)}
                            disabled={draftingField === field || submitting}
                          >
                            {draftingField === field ? (
                              <><Loader2 className="h-3 w-3 animate-spin" />Combining…</>
                            ) : (
                              <><Sparkles className="h-3 w-3 text-blue-400" />
                                {customValues[field]?.trim() ? "Regenerate with AI" : "Combine both with AI"}</>
                            )}
                          </Button>
                          <span className="text-label-2xs text-subtle-foreground">
                            Merges both values — you can still edit the result.
                          </span>
                        </div>
                        {draftNotes[field] && (
                          <p className="flex items-start gap-1 text-label-2xs text-amber-500/90 leading-relaxed">
                            <AlertTriangle className="h-2.5 w-2.5 shrink-0 mt-0.5" />
                            {draftNotes[field]}
                          </p>
                        )}
                      </div>
                    )}

                    {suggestion?.reason && (
                      <p className="flex items-start gap-1 text-label-2xs text-muted-foreground/70 leading-relaxed">
                        <Sparkles className="h-2.5 w-2.5 shrink-0 mt-0.5 text-blue-400/70" />
                        {suggestion.reason}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {!showPicker && (
          <DialogFooter className="flex-shrink-0 px-5 py-3.5 border-t border-border/50">
            {mergeSummary ? (
              <Button onClick={handleDoneAfterMerge}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleBack} disabled={submitting}>
                  {backLabel}
                </Button>
                {showDiff && hasConflicts && (
                  <Button onClick={() => handleApply()} disabled={submitting || loading}>
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                  </Button>
                )}
                {/* Non-multi-match mode never reaches the A/B screen, so its "nothing
                    conflicts, just link them" confirm still lives here. isEntityMerge must
                    go through handleApply — that's the only path that runs
                    merge_entity_into's relationship/permission/integration re-point sweep. */}
                {showDiff && !hasConflicts && isMultiMatch && (
                  <Button
                    onClick={isEntityMerge ? () => handleApply() : () => targetEntityId && onResolved(targetEntityId)}
                    disabled={submitting}
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm, this is the same entity"}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** One side of the A-vs-B comparison. Deliberately shows the same four fields for both
 * sides so they line up visually — an em dash where a side has nothing is more useful
 * than a shorter card, since "B has no role recorded" is itself a reason to pick A. */
function VersionCard({
  tone, letter, heading, subheading, icon, name, role, location, description,
}: {
  tone: "incoming" | "existing";
  letter: string;
  heading: string;
  subheading: string;
  icon: ReactNode;
  name: string | null;
  role: string | null;
  location: string | null;
  description: string | null;
}) {
  const incoming = tone === "incoming";
  return (
    <div
      className={cn(
        "rounded-lg border p-3.5 flex flex-col gap-1.5",
        incoming ? "border-blue-500/25 bg-blue-500/[0.04]" : "border-border/60 bg-muted/[0.15]",
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "h-5 w-5 rounded flex items-center justify-center text-label-2xs font-bold shrink-0",
            incoming ? "bg-blue-500/20 text-blue-400" : "bg-muted-foreground/15 text-muted-foreground",
          )}
        >
          {letter}
        </span>
        <div className="min-w-0">
          <p className={cn("text-label-2xs font-semibold uppercase tracking-wide flex items-center gap-1",
            incoming ? "text-blue-400/80" : "text-muted-foreground/70")}>
            {icon}
            {heading}
          </p>
          <p className="text-label-2xs text-subtle-foreground">{subheading}</p>
        </div>
      </div>
      <p className="text-sm font-medium text-foreground break-words">{name ?? "—"}</p>
      <div className="flex flex-col gap-1 text-label-xs text-muted-foreground/80">
        <span className="flex items-start gap-1">
          <Briefcase className="h-3 w-3 shrink-0 mt-0.5" />
          <span className="break-words">{role ?? "—"}</span>
        </span>
        <span className="flex items-start gap-1">
          <MapPin className="h-3 w-3 shrink-0 mt-0.5" />
          <span className="break-words">{location ?? "—"}</span>
        </span>
      </div>
      <p className="text-label-xs text-muted-foreground/70 leading-relaxed line-clamp-4">
        {description ?? "—"}
      </p>
    </div>
  );
}

/** Full-width option row on the decision screen — a real choice the user clicks, not a
 * radio they then have to confirm somewhere else. */
function ChoiceRow({
  icon, title, subtitle, badge, onClick, disabled, busy,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  badge?: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex items-start gap-3 text-left rounded-lg border border-border/50 px-3.5 py-3 transition-all",
        "hover:border-blue-500/40 hover:bg-blue-500/[0.03]",
        disabled && "opacity-50 cursor-not-allowed hover:border-border/50 hover:bg-transparent",
      )}
    >
      <span className="shrink-0 mt-0.5">
        {busy ? <Loader2 className="h-4 w-4 animate-spin text-blue-400" /> : icon}
      </span>
      <span className="flex flex-col gap-0.5 min-w-0">
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{title}</span>
          {badge && (
            <Badge variant="outline" className="text-label-2xs px-1.5 py-0 border-blue-500/40 text-blue-400 shrink-0">
              {badge}
            </Badge>
          )}
        </span>
        <span className="text-label-xs text-muted-foreground/70 leading-relaxed">{subtitle}</span>
      </span>
    </button>
  );
}
